import Category from '@/ui/components/category/Category'
import { Fragment, useEffect, useState } from 'react'
import RollableTags from '@/ui/components/category/RollableTags'
import { useDispatch, useSelector } from 'react-redux'
import SquaresPattern from '@/ui/icons/SquaresPattern'
import { loadBranchPromoCategories } from '@/functions//loaderGraphQl'
import Banner from '@/ui/components/banner/Banner'
import { AxiosClient } from '@/axios/axios.client'
import { BannerTypeEnum, FooterTypeEnum } from '@/constants/constants'
import Layout from '@/ui/components/shared/Layout'
import LoaderComponent from '@/ui/components/shared/LoaderComponent'
import { performScroll, setupScrollElement } from '@/functions/ui/scroller'
import Navigation from '@/ui/components/shared/Navigation'
import { currentTagSelector, menuSelector, scrollSelector } from '@/selectors/selectors'
import { completeMenu, loadMenu } from '@/functions/menuLoader/menuLoader'
import { setMenu } from '@/redux/actions/menuAction'
import Scroller from '@/ui/components/shared/Scroller'
import { isCategoryNotEmpty, webalizeName } from '@/functions/common/helper'
import SearchBar from '@/ui/components/searchBar/SearchBar'
import ProductPlaceHolder from '@/ui/components/placeholders/ProductPlaceHolder'
import RowProductPlaceHolder from '@/ui/components/placeholders/RowProductPlaceHolder'
import { captureException } from '@sentry/nextjs'
import Head from 'next/head'
import { setCurrentTag } from '@/redux/actions/tagAction'
import { useRouter } from 'next/router'

/**
 * Server side rendering
 *
 * Calls node BE
 * Get detail about current branch
 *
 * If branch is not active or not found it redirects to error page
 * Otherwise returns data to page
 * */
export async function getServerSideProps(context) {
    const { branch } = context.params
    let data = null
    if (!branch.includes('.')) {
        await AxiosClient.get(`/v1/web/hp/${branch}/load?category`)
            .then(res => {
                data = res.data.data
            })
            .catch(err => {
                // captureException(err)
                console.error(err)
            })
    }
    if (!data)
        return {
            notFound: true
        }
    else if (!data.branch.active)
        return {
            notFound: true
        }
    else
        return {
            props: {
                data
            }
        }
}

const Index = ({ data }) => {
    const router = useRouter()
    const menu = useSelector(menuSelector)
    const branchId = returnSpeedloID()
    // const orderDeliveryType = useSelector(orderDeliveryTypeSelector);
    const scrollToElement = useSelector(scrollSelector)
    const currentTag = useSelector(currentTagSelector)

    let dispatch = useDispatch()

    const [menuCompleted, setMenuCompleted] = useState(false)
    const [searchValue, setSearchValue] = useState(null)
    const [formattedMenu, setFormattedMenu] = useState(null)
    const [promoCategories, setPromoCategories] = useState({
        header: {
            loaded: false,
            data: null
        },
        bottom: {
            loaded: false,
            data: null
        }
    })
    const [banners, setBanners] = useState({
        insideCategory: null,
        bottomCategory: null
    })

    const navigationLevels = [
        {
            name: currentTag.label
        }
    ]
    useEffect(() => {
        if (router) {
            if (!currentTag && menu) {
                let foundMatch = null
                for (let i = 0; i < menu.tags.length; i++) {
                    if (webalizeName(menu.tags[i].label) === router.query.category) {
                        foundMatch = menu.tags[i]
                    }
                }
                if (foundMatch) dispatch(setCurrentTag(foundMatch))
                else router.replace(`/${router.query.branch}/`)
            }
        }
    }, [currentTag, menu, router.query])

    useEffect(() => {
        if (menu === null || menu === undefined) {
            loadMenu(branchId, dispatch).then(onlineMenu => {
                if (onlineMenu) {
                    dispatch(setMenu({ tags: onlineMenu.tags }))
                }
            })
        } else {
            dispatch(setMenu({ tags: menu.tags }))
        }
        return () => {
            setMenuCompleted(false)
        }
    }, [])

    /**
     * Scrolls to element if any is present
     * Is updated whenever scrollToElement is updated by dispatch
     * */
    useEffect(() => {
        if (scrollToElement) {
            performScroll(scrollToElement)
            setupScrollElement(dispatch, null)
        }
    }, [dispatch, scrollToElement])

    /**
     * Creating menu for current page
     * */
    useEffect(() => {
        if (menu) {
            for (let i = 0; i < menu.tags.length; i++) {
                if (menu.tags[i].label === currentTag.label) {
                    setFormattedMenu(menu.tags[i].categories)
                }
            }
        }
    }, [currentTag, menu])

    /*
     *   Complete menu
     * */
    useEffect(() => {
        if (menu !== null && branchId !== null && !menuCompleted) {
            completeMenu(menu.tags, branchId, dispatch).then(onlineMenu => {
                setMenuCompleted(true)
                dispatch(setMenu({ tags: onlineMenu }))
            })
        }
    }, [menu])

    /**
     * Loading banners for current branch and her categories
     * */
    useEffect(() => {
        AxiosClient.get(`/v1/web/hp/${branchId}/load/category`)
            .then(res => {
                setBanners({
                    insideCategory: res.data.data.categoryInside,
                    bottomCategory: res.data.data.categoryBottom
                })
            })
            .catch(err => {
                captureException(err)
                console.error(err)
            })
        return () => {
            setBanners({
                insideCategory: null,
                bottomCategory: null
            })
        }
    }, [branchId])

    /**
     * Loading promo categories
     * Loads categories display in Header and Footer
     * */
    useEffect(() => {
        if (branchId !== null && !promoCategories.header.loaded && !promoCategories.bottom.loaded) {
            loadBranchPromoCategories(branchId, dispatch).then(categories => {
                setPromoCategories({
                    header: {
                        loaded: true,
                        data: categories[0].wareCategories ? categories[0].wareCategories : null
                    },
                    bottom: {
                        loaded: true,
                        data: categories[1].wareCategories ? categories[1].wareCategories : null
                    }
                })
            })
        }
        return () => {
            setPromoCategories({
                header: {
                    loaded: false,
                    data: null
                },
                bottom: {
                    loaded: false,
                    data: null
                }
            })
        }
    }, [])

    function returnSpeedloID() {
        let speedloBranchId = null
        if (data !== null) speedloBranchId = data.branch.speedloId

        return speedloBranchId
    }

    /**
     * Renders top promo categories
     * */
    const renderTopPromoCategories = data => {
        let categories = []
        data.map(category => {
            categories.push(
                <Fragment key={category.id}>
                    <Category
                        wareCategory={category}
                        numberOfLines={2}
                        rowDisplay={true}
                        color={currentTag.color}
                        isPromoCategory={true}
                        promoTag={'PROMO_HEADER'}
                    />
                </Fragment>
            )
        })
        return categories
    }

    /**
     * Renders bottom promo categories
     * */
    const renderBottomPromoCategories = data => {
        let categories = []
        data.map(category => {
            categories.push(
                <Fragment key={category.id}>
                    <Category
                        titleVisible={false}
                        wareCategory={category}
                        numberOfLines={2}
                        rowDisplay={false}
                        color={currentTag.color}
                        isPromoCategory={true}
                        promoTag={'PROMO_FOOTER'}
                    />
                </Fragment>
            )
        })
        return categories
    }

    /**
     * Renders categories with banner inside
     * */
    const renderCategories = (data, banner) => {
        let categories = []
        let passBanner = null
        data.map((category, index) => {
            if (index === 0) passBanner = banner
            else passBanner = null
            categories.push(
                <Fragment key={category.id}>
                    <Category wareCategory={category} numberOfLines={2} color={currentTag.color} banner={passBanner} />
                </Fragment>
            )
        })
        return categories
    }

    // TODO : upgrade for showing no RESULTS
    function filterCategories(searchValue, categories) {
        let filteredCategories = []

        if (searchValue) {
            categories?.map(category => {
                let deepCopyCategory = {
                    id: category.id,
                    nameLabel: category.nameLabel,
                    recipes: []
                }
                category.recipes.map(product => {
                    deepCopyCategory.recipes.push(product)
                })
                filteredCategories.push(deepCopyCategory)
            })

            // Filter categories
            filteredCategories = filteredCategories.filter(category =>
                category.recipes.filter(
                    value =>
                        value.nameLabel.toLowerCase().includes(searchValue.toLowerCase()) ||
                        (value.descriptionLabel &&
                            value.descriptionLabel.toLowerCase().includes(searchValue.toLowerCase()))
                )
            )
            filteredCategories = filteredCategories.filter(
                category =>
                    (category.recipes = category.recipes.filter(
                        value =>
                            value.nameLabel.toLowerCase().includes(searchValue.toLowerCase()) ||
                            (value.descriptionLabel &&
                                value.descriptionLabel.toLowerCase().includes(searchValue.toLowerCase()))
                    ))
            )
            filteredCategories = filteredCategories.filter(value => value.recipes.length !== 0)
        } else {
            filteredCategories = categories
        }

        return filteredCategories
    }

    return (
        <Layout customData={data} showCity={true} visibleCart={true} footerType={FooterTypeEnum.HP_BRANCH}>
            <Head>
                <title>
                    {data.branch.name} | {currentTag.label}
                </title>
            </Head>
            {formattedMenu && (
                <RollableTags
                    title={currentTag.label}
                    menu={menu.tags}
                    formattedMenu={formattedMenu}
                    color={currentTag.color}
                />
            )}
            <Navigation levels={navigationLevels} />
            <Scroller />
            <RowProductPlaceHolder data={promoCategories.header.loaded}>
                <div className={'lg:container lg:mx-auto  pt-4'}>
                    <div className={' max-w-[50ch] sm:max-w-[40ch]  xs:mx-5'}>
                        <SearchBar updateSearchValue={setSearchValue} />
                    </div>
                </div>
                {isCategoryNotEmpty(promoCategories.header.data) && (
                    <div className={'lg:container mx-auto'}>
                        {renderTopPromoCategories(filterCategories(searchValue, promoCategories.header.data))}
                    </div>
                )}
            </RowProductPlaceHolder>
            <ProductPlaceHolder data={formattedMenu}>
                {formattedMenu && (
                    <>{renderCategories(filterCategories(searchValue, formattedMenu), banners.insideCategory)}</>
                )}
            </ProductPlaceHolder>
            <div className={'bg-turbo-background  text-center relative mt-24 2xl:mt-32'}>
                <div className={'w-full absolute -top-9'}>
                    <SquaresPattern color={currentTag.color} />
                </div>
                <div className={'justify-center color text-turbo-secondary pt-16 xl:pt-28'}>
                    <LoaderComponent loaded={promoCategories.bottom.loaded}>
                        {isCategoryNotEmpty(filterCategories(searchValue, promoCategories.bottom.data)) && (
                            <Fragment>
                                <div className={'pb-7 xl:mb-20'}>
                                    <div className={'leading-none fk-screamer-title lg:inline-block'}>
                                        Nevybrali jste si?
                                    </div>
                                    <div
                                        className={
                                            'leading-none text-turbo-primary fk-screamer-title lg:inline-block lg:ml-4'
                                        }
                                    >
                                        Zkuste třeba:
                                    </div>
                                </div>
                                {renderBottomPromoCategories(promoCategories.bottom.data)}
                            </Fragment>
                        )}
                    </LoaderComponent>

                    {banners && banners.bottomCategory && (
                        <>
                            <Banner bannerData={banners.bottomCategory} bannerType={BannerTypeEnum.BRANCH_BOTTOM} />
                            <div className={'bg-white'}>
                                <SquaresPattern />
                            </div>
                        </>
                    )}
                </div>
            </div>
        </Layout>
    )
}
export default Index
